/**
 * FunnelNodeDrilldownModal - Modal de drill-down por node do funil
 * 
 * Exibe a listagem de sessões que passaram por um node específico,
 * reutilizando a mesma tabela da aba "Resultado" (FlowResultsSessionsTable).
 * Acionado ao clicar em um node no canvas do Funil Tempo Real.
 * 
 * @release v1.0.0 - 2026-03-10
 */

import { useState, useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { FlowResultsSessionsTable, type NodeColumn } from './FlowResultsSessionsTable';
import { useFlowResultsPaginated, type FlowSessionFilters } from '@/hooks/useFlowResultsPaginated';
import { supabase } from '@/integrations/supabase/client';

interface FunnelNodeDrilldownModalProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para fechar o modal */
  onOpenChange: (open: boolean) => void;
  /** ID do fluxo */
  flowId: string;
  /** ID do node selecionado */
  nodeId: string;
  /** Label legível do node */
  nodeLabel: string;
  /** Contagem de leads exibida no badge do funil (fallback durante loading) */
  entries?: number;
  /** Período ativo do funil (ex: '24h', '7d', '30d', '90d') para sincronizar filtro de data */
  period?: string;
}

/**
 * Modal que exibe sessões filtradas por node específico do funil
 */
export function FunnelNodeDrilldownModal({
  open,
  onOpenChange,
  flowId,
  nodeId,
  nodeLabel,
  entries,
  period,
}: FunnelNodeDrilldownModalProps) {
  // Estado de paginação interno do modal
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Filtros internos do modal (com nodeId pré-definido)
  const [filters, setFilters] = useState<FlowSessionFilters>({});

  // Calcula startDate baseado no período ativo do funil para sincronizar com a RPC de stats
  const periodStartDate = useMemo(() => {
    const map: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };
    const days = map[period || '7d'] || 7;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
  }, [period]);

  // Combina filtros do usuário com o nodeId fixo e período sincronizado
  const combinedFilters: FlowSessionFilters = useMemo(() => ({
    ...filters,
    nodeId,
    startDate: filters.startDate || periodStartDate,
  }), [filters, nodeId, periodStartDate]);

  // Busca sessões paginadas filtradas pelo node
  const {
    items: sessions,
    totalCount,
    totalPages,
    isLoading,
    isFetching,
    refetch,
  } = useFlowResultsPaginated(
    open ? flowId : undefined, // Só busca quando o modal está aberto
    combinedFilters,
    { page: currentPage, pageSize, sortField: 'started_at', sortDirection: 'desc' },
  );

  // Busca nodes do fluxo para colunas dinâmicas
  const { data: flow } = useQuery({
    queryKey: ['flow-results-basic', flowId],
    queryFn: async () => {
      const { data } = await supabase
        .from('flows')
        .select('nodes')
        .eq('id', flowId)
        .single();
      return data;
    },
    enabled: !!flowId && open,
  });

  // Extrai colunas dinâmicas dos nodes interativos
  const nodeColumns: NodeColumn[] = useMemo(() => {
    if (!flow?.nodes) return [];
    const nodes = flow.nodes as Array<{
      id: string;
      type: string;
      data?: { label?: string; config?: { variableName?: string; question?: string; nickname?: string } };
    }>;
    const excludedTypes = ['start', 'end'];
    return nodes
      .filter(n => !excludedTypes.includes(n.type))
      .map(n => ({
        nodeId: n.id,
        label: n.data?.config?.nickname || n.data?.label || n.data?.config?.question || n.data?.config?.variableName || n.type,
        nodeType: n.type,
      }));
  }, [flow?.nodes]);

  // Handler de mudança de filtros (reseta paginação)
  const handleFiltersChange = useCallback((newFilters: FlowSessionFilters) => {
    // Preserva nodeId fixo, permite outros filtros
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] flex flex-col p-0 gap-0">
        {/* Cabeçalho do modal com info do node */}
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-semibold">
              Sessões no node: {nodeLabel}
            </DialogTitle>
            <Badge variant="secondary" className="flex items-center gap-1 text-xs">
              <Users className="h-3 w-3" />
              {(isLoading ? (entries ?? 0) : totalCount).toLocaleString('pt-BR')} leads
            </Badge>
          </div>
          <DialogDescription className="text-xs text-muted-foreground">
            Listagem de todas as sessões que passaram por este node do fluxo
          </DialogDescription>
        </DialogHeader>

        {/* Tabela de sessões reutilizada */}
        <div className="flex-1 overflow-auto p-4">
          <FlowResultsSessionsTable
            sessions={sessions}
            nodeColumns={nodeColumns}
            flowId={flowId}
            totalCount={totalCount}
            totalPages={totalPages}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            isLoading={isLoading}
            isFetching={isFetching}
            onRefresh={refetch}
            isRefreshing={isFetching}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
